import React, { PureComponent } from 'react';

import moment from 'moment';

import { CalendarAPI } from '@philips-emr/tws-components-api';
import { RangeState } from '@philips-emr/tws-components-api/lib/constants';
import { ButtonTheme, CalendarMode } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { array, arrayOf, bool, element, eventListener, instanceOf, number, object, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, enumValues, getLostProps, isFunction, propagateProps } from '@philips-emr/tws-utils';

import { Button } from '..';
import { ReactComponent as AddIcon } from '../../../resources/assets/components/plus-circle-sm-dark.svg';
import Loader from '../loader/loader';

import './calendar.scss';
import './weekly-calendar.scss';

import {
  ModeChanger,
  MonthlyCalendar,
  TimeSpan,
  Weekdays,
  WeeklyCalendar,
} from './components';

@AwesomeComponent({ type: ComponentType.DATA_DISPLAY, api: CalendarAPI })
class Calendar extends PureComponent {
  static propTypes = {
    canAddNew: bool,
    canChangeMode: bool,
    children: oneOfType([element, arrayOf(element)]),
    data: arrayOf(object).notEnumerable(),
    date: oneOfType([instanceOf(moment), string]),
    endOfDay: number,
    loading: bool,
    mode: oneOf(enumValues(CalendarMode)),
    onAddNew: eventListener,
    onClick: eventListener,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    onEventClick: eventListener,
    onNextPage: eventListener,
    onPrevPage: eventListener,
    ranges: array.notEnumerable(),
    rangeState: oneOf(enumValues(RangeState)),
    startOfDay: number,
    uuid: string,
  };

  static defaultProps = {
    canAddNew: false,
    canChangeMode: false,
    data: [],
    date: moment.utc(),
    endOfDay: 18,
    loading: false,
    mode: CalendarMode.MONTHLY,
    rangeState: RangeState.ENABLED,
    ranges: [],
    startOfDay: 8,
  };

  constructor(props) {
    super(props);

    this.state = {
      date: moment(props.date),
      mode: props.mode,
    };
  }

  componentDidMount() {
    this.onDateChange(this.getPeriod());
  }

  getPeriod() {
    const { mode } = this.state;

    return mode === CalendarMode.MONTHLY ? 'month' : 'week';
  }

  renderCalendarBlocks = () => {
    const { startOfDay, ranges, rangeState, onEventClick, loading, endOfDay, data, canAddNew } = this.props;
    const { date, mode } = this.state;
    const rangeEnabled = rangeState === RangeState.ENABLED;

    if (loading) {
      return null;
    }

    if (mode === CalendarMode.WEEKLY) {
      return (
        <WeeklyCalendar
          date={date}
          endOfDay={endOfDay}
          events={data}
          mode={mode}
          startOfDay={startOfDay}
        />
      );
    }

    if (mode === CalendarMode.MONTHLY) {
      return (
        <MonthlyCalendar
          canAddNew={canAddNew}
          date={date}
          events={data}
          mode={mode}
          onAddNew={this.onAddNew}
          onDateClick={this.onDateClick}
          onEventClick={onEventClick}
          rangeEnabled={rangeEnabled}
          ranges={ranges}
        />
      );
    }
  };

  renderLoader() {
    const { loading } = this.props;

    if (!loading) {
      return null;
    }

    return (
      <div className="c-calendar__loader o-loader">
        <Loader size={64} />
      </div>
    );
  }

  onDateClick = (value) => {
    this.setState({
      date: moment(value),
    });
  };

  getBoundaries = () => {
    const { mode, date } = this.state;

    const boundariesByMode = {
      [CalendarMode.MONTHLY]: [moment(date).startOf('month').add(-6, 'days'), moment(date).endOf('month').add(6, 'days')],
      [CalendarMode.WEEKLY]: [moment(date).startOf('week'), moment(date).endOf('week')],
    };

    return boundariesByMode[mode];
  };

  onAddNew = ({ date } = {}) => {
    const { onAddNew } = this.props;
    const isoDate = moment.utc(date).format('YYYY-MM-DD');

    isFunction(onAddNew) && onAddNew({ date: isoDate });
  }

  onDateChange = (period) => {
    const { onNextPage, onPrevPage } = this.props;
    const { date } = this.state;

    const [startDate, endDate] = this.getBoundaries();

    const params = {
      currentDate: date.toISOString(),
      endDate: endDate.toISOString(),
      startDate: startDate.toISOString(),
    };

    const fn = period > 0 ? onNextPage : onPrevPage;
    fn && fn({ page: period, params, mergeParams: true });
  };

  onArrowClick = (value) => {
    const { loading } = this.props;

    if (loading) {
      return;
    }

    const { mode } = this.state;
    const period = this.getPeriod();

    this.setState(({ date: prevDate }) => {
      const date = mode === CalendarMode.WEEKLY
        ? moment(prevDate).add(value, period).startOf(period)
        : moment(prevDate).add(value, period);

      return { date };
    }, () => this.onDateChange(period))
  };

  onChangeVisualizationType = (mode) => {
    this.setState({ mode });
  };

  onTimeSpanChange = (value) => {
    this.setState({ date: moment.utc(value) }, () =>
      this.onDateChange(this.getPeriod())
    );
  };

  render() {
    const { mode, date } = this.state;
    const { canAddNew, canChangeMode, children, onNextPage, onPrevPage, uuid } = this.props;

    const Container = ContainerProvider.provide(this);
    const lostProps = getLostProps(Calendar.propTypes, this.props);

    const rootClasses = {
      'c-calendar': true,
      'c-calendar--monthly': mode === CalendarMode.MONTHLY,
      'c-calendar--weekly': mode === CalendarMode.WEEKLY,
    };

    return (
      <div className={classNames(rootClasses)}>
        <div className="c-calendar__header u-pull-schematics">
          <TimeSpan date={date} onArrowClick={this.onArrowClick} onChange={this.onTimeSpanChange} mode={mode} />

          <Container className="c-calendar__actions" uuid={uuid}>
            {propagateProps(children, { ...lostProps, onNextPage, onPrevPage })}

            <ModeChanger
              canChangeMode={canChangeMode}
              mode={mode}
              onChange={this.onChangeVisualizationType}
            />

            {canAddNew && (
              <Button
                className="c-calendar__add-new"
                icon={<AddIcon />}
                onClick={this.onAddNew}
                theme={ButtonTheme.OUTLINE}
                value={msg('tws.calendar.add-new')}
              />
            )}
          </Container>
        </div>
        <div className="c-calendar__content">
          <Weekdays date={date} mode={mode} />

          <div className="c-calendar__blocks">
            {this.renderCalendarBlocks()}
          </div>

          {this.renderLoader()}

          {/* <div className="c-calendar__not-found o-not-found u-hide">
            <div className="o-not-found__img u-hide@xsm" />
            <span className="o-not-found__title" />
            <span className="o-not-found__subtitle" />
          </div> */}
        </div>
      </div>
    );
  }
}

export default Calendar;
